import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Typography,
  Chip,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Delete,
  Edit,
} from '@mui/icons-material';
import { Transaction, Category } from '../types';
import { formatCurrency } from '../utils/dateUtils';

interface MobileTransactionListProps {
  transactions: Transaction[];
  categories: Category[];
  onEditTransaction?: (transaction: Transaction) => void;
  onDeleteTransaction?: (transactionId: string) => void;
  onEdit?: (transaction: Transaction) => void;
  onDelete?: (transactionId: string) => void;
  maxItems?: number;
}

const MobileTransactionList: React.FC<MobileTransactionListProps> = ({
  transactions,
  categories,
  onEditTransaction,
  onDeleteTransaction,
  onEdit,
  onDelete,
  maxItems,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const displayTransactions = maxItems 
    ? transactions.slice(0, maxItems)
    : transactions;

  const getCategoryColor = (categoryName: string) => {
    const category = categories.find(c => c.name === categoryName);
    return category?.color || theme.palette.primary.main;
  };

  const formatDate = (dateInput: string | Date) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    return isMobile 
      ? date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
      : date.toLocaleDateString('fr-FR');
  };

  if (transactions.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Aucune transaction trouvée
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <List sx={{ p: 0 }}>
        {displayTransactions.map((transaction, index) => (
          <React.Fragment key={transaction.id}>
            <ListItem
              sx={{
                py: isMobile ? 1.5 : 1,
                px: isMobile ? 2 : 3,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant={isMobile ? 'body2' : 'body1'}
                    sx={{
                      fontWeight: 'bold',
                      color: transaction.type === 'income' 
                        ? theme.palette.success.main 
                        : theme.palette.error.main,
                      minWidth: isMobile ? '80px' : '100px',
                      textAlign: 'right',
                    }}
                  >
                    {transaction.type === 'income' ? '+' : '-'}
                    {formatCurrency(transaction.amount)}
                  </Typography>
                  {!isMobile && (onEdit || onDelete || onEditTransaction || onDeleteTransaction) && (
                    <Box sx={{ ml: 1 }}>
                      {(onEdit || onEditTransaction) && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (onEditTransaction) onEditTransaction(transaction);
                            else if (onEdit) onEdit(transaction);
                          }}
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                      {(onDelete || onDeleteTransaction) && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (onDeleteTransaction) onDeleteTransaction(transaction.id);
                            else if (onDelete) onDelete(transaction.id);
                          }}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: isMobile ? 40 : 56 }}>
                <Avatar
                  sx={{
                    bgcolor: getCategoryColor(transaction.category),
                    width: isMobile ? 32 : 40,
                    height: isMobile ? 32 : 40,
                  }}
                >
                  {transaction.type === 'income' ? (
                    <TrendingUp fontSize={isMobile ? 'small' : 'medium'} />
                  ) : (
                    <TrendingDown fontSize={isMobile ? 'small' : 'medium'} />
                  )}
                </Avatar>
              </ListItemIcon>
              
              <ListItemText
                primary={
                  <Box>
                    <Typography
                      variant={isMobile ? 'body2' : 'body1'}
                      sx={{ fontWeight: 'medium' }}
                    >
                      {transaction.description}
                    </Typography>
                    {isMobile && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={transaction.category}
                          size="small"
                          sx={{
                            bgcolor: getCategoryColor(transaction.category),
                            color: 'white',
                            fontSize: '0.75rem',
                            height: 20,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(transaction.date)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                }
                secondary={
                  !isMobile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={transaction.category}
                        size="small"
                        sx={{
                          bgcolor: getCategoryColor(transaction.category),
                          color: 'white',
                          fontSize: '0.75rem',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(transaction.date)}
                      </Typography>
                    </Box>
                  )
                }
              />
            </ListItem>
            {index < displayTransactions.length - 1 && (
              <Divider variant={isMobile ? 'fullWidth' : 'inset'} />
            )}
          </React.Fragment>
        ))}
      </List>
      
      {maxItems && transactions.length > maxItems && (
        <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            +{transactions.length - maxItems} autres transactions
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MobileTransactionList;
